/**
 * P2025 Product Variants Handler
 * Manages variant selection, price updates, and form submission
 */
class P2025VariantSelector {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.section;
    this.productUrl = container.dataset.productUrl;
    
    const dataScript = container.querySelector('[data-variant-data]');
    if (!dataScript) return;
    
    this.variantData = JSON.parse(dataScript.textContent);
    this.optionWrappers = container.querySelectorAll('.p2025-variant-scale__wrapper');
    this.currentVariant = null;
    this.selectedOptions = {};
    
    this.variantInput = this.findVariantInput();
    if (!this.variantInput) return;
    
    this.productForm = this.findProductForm();
    
    this.init();
  }

  findVariantInput() {
    // Try product-form element first
    const productFormElement = document.querySelector('product-form');
    if (productFormElement) {
      const form = productFormElement.querySelector('form');
      if (form) {
        const input = form.querySelector('input[name="id"], .product-variant-id');
        if (input) return input;
      }
    }
    
    // Fallback selectors
    return document.querySelector('.product-variant-id') ||
           document.querySelector('input[name="id"]');
  }

  findProductForm() {
    const productFormElement = document.querySelector('product-form');
    if (productFormElement) {
      return productFormElement.querySelector('form');
    }
    return document.querySelector('form[action*="/cart/add"]');
  }

  init() {
    this.initializeFromUrl();
    this.bindEvents();
    this.initTooltips();
  }

  initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    
    let defaultVariant = null;
    
    if (urlVariantId) {
      defaultVariant = this.variantData.variants.find(v => 
        v.id.toString() === urlVariantId
      );
    }
    
    if (!defaultVariant && this.variantData.defaultVariantId) {
      defaultVariant = this.variantData.variants.find(v => 
        v.id === this.variantData.defaultVariantId
      );
    }
    
    if (!defaultVariant) {
      defaultVariant = this.variantData.variants[0];
    }
    
    if (!defaultVariant) return;
    
    this.currentVariant = defaultVariant;
    this.updateVariantInput(defaultVariant.id);
    this.setActiveButtons(defaultVariant);
  }

  setActiveButtons(variant) {
    this.optionWrappers.forEach(wrapper => {
      const position = parseInt(wrapper.dataset.optionPosition);
      const value = variant.options[position - 1];
      
      if (!value) return;
      
      wrapper.querySelectorAll('.p2025-variant-button').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.optionValue === value);
        if (btn.dataset.optionValue === value) {
          this.selectedOptions[position] = value;
        }
      });
    });
  }

  bindEvents() {
    // Option button clicks
    this.optionWrappers.forEach(wrapper => {
      const buttons = wrapper.querySelectorAll('.p2025-variant-button');
      const position = parseInt(wrapper.dataset.optionPosition);
      
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          buttons.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.selectedOptions[position] = btn.dataset.optionValue;
          this.updateVariant();
        });
      });
    });

    // Form submission
    if (this.productForm) {
      this.productForm.addEventListener('submit', (e) => this.handleSubmit(e), true);
    }
  }

  getSelectedOptionValues() {
    const values = [];
    this.optionWrappers.forEach(wrapper => {
      const position = parseInt(wrapper.dataset.optionPosition);
      const activeBtn = wrapper.querySelector('.p2025-variant-button.active');
      if (activeBtn) {
        values[position - 1] = activeBtn.dataset.optionValue;
      }
    });
    return values;
  }

  findMatchingVariant(optionValues) {
    return this.variantData.variants.find(variant => 
      variant.options.every((opt, idx) => {
        const selected = optionValues[idx];
        if (!selected) return !opt; // Both empty is a match
        if (!opt) return false;
        return opt.toString().trim().toLowerCase() === selected.toString().trim().toLowerCase();
      })
    );
  }

  getOptionCount() {
    // options_with_values from Shopify includes position, name, values
    if (Array.isArray(this.variantData.options)) {
      return this.variantData.options.length;
    }
    return this.optionWrappers.length;
  }

  updateVariant() {
    const optionValues = this.getSelectedOptionValues();
    
    if (optionValues.length !== this.getOptionCount() ||
        optionValues.some(v => v === undefined)) {
      return;
    }
    
    const variant = this.findMatchingVariant(optionValues);
    if (!variant) return;
    
    this.currentVariant = variant;
    this.updateVariantInput(variant.id);
    this.updateUrl(variant.id);
    this.fetchVariantData(variant);
  }

  updateVariantInput(variantId) {
    const id = String(variantId);
    
    document.querySelectorAll('input[name="id"]').forEach(input => {
      input.value = id;
      input.disabled = false;
    });
    
    document.querySelectorAll('product-form').forEach(pf => {
      const form = pf.form || pf.querySelector('form');
      if (form) {
        const input = form.querySelector('input[name="id"], .product-variant-id');
        if (input) {
          input.value = id;
          input.disabled = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  updateUrl(variantId) {
    if (window.history?.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }
  }

  async fetchVariantData(variant) {
    const url = `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      
      this.updatePriceFromHtml(html);
      this.updateInventoryFromHtml(html);
      this.updateButtonFromHtml(html);
      this.syncVariantIdFromHtml(html, variant);
      this.dispatchVariantChange(variant, html);
      
    } catch (error) {
      console.error('Error fetching variant data:', error);
    }
  }

  updatePriceFromHtml(html) {
    const source = html.querySelector('.c-product-pricepad__title');
    const dest = document.querySelector('.c-product-pricepad__title');
    
    if (!source || !dest) return;
    
    const sourcePrices = source.querySelectorAll('.price, .price--large, [class*="price"]');
    const destPrices = dest.querySelectorAll('.price, .price--large, [class*="price"]');
    
    sourcePrices.forEach((srcPrice, i) => {
      if (destPrices[i]) {
        destPrices[i].outerHTML = srcPrice.outerHTML;
      }
    });
    
    // Update mobile CTA price
    const mobilePrice = document.querySelector('.c-product-mobile-cta__price .price, .c-product-mobile-cta__price [class*="price"]');
    if (mobilePrice) {
      const sourcePrice = source.querySelector('.price, [class*="price"]');
      if (sourcePrice) {
        mobilePrice.outerHTML = sourcePrice.outerHTML;
      }
    }
  }

  updateInventoryFromHtml(html) {
    const source = html.getElementById(`Inventory-${this.sectionId}`);
    const dest = document.getElementById(`Inventory-${this.sectionId}`);
    if (source && dest) {
      dest.innerHTML = source.innerHTML;
    }
  }

  updateButtonFromHtml(html) {
    const source = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
    const dest = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    
    if (!source || !dest) return;
    
    dest.disabled = source.disabled;
    const srcSpan = source.querySelector('span');
    const destSpan = dest.querySelector('span');
    if (srcSpan && destSpan) {
      destSpan.innerHTML = srcSpan.innerHTML;
    }
  }

  syncVariantIdFromHtml(html, variant) {
    const fetchedInput = html.querySelector('input[name="id"]');
    if (fetchedInput?.value && fetchedInput.value !== String(variant.id)) {
      variant.id = parseInt(fetchedInput.value);
      this.currentVariant = variant;
      this.updateVariantInput(variant.id);
    }
  }

  dispatchVariantChange(variant, html) {
    window.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant }
    }));
    
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.variantChange, {
        data: { sectionId: this.sectionId, html, variant }
      });
    }
    
    // Update Klarna On-Site Messaging with new price
    this.updateKlarnaPrice(variant, html);
  }

  updateKlarnaPrice(variant, html) {
    // Try to get price from fetched HTML
    let priceInCents = null;
    const priceEl = html.querySelector('[data-product-price]') || 
                    html.querySelector('.price__regular .price-item--regular') ||
                    html.querySelector('.price-item--sale') ||
                    html.querySelector('.price-item');
    
    if (priceEl) {
      // Extract numeric value and convert to cents
      const priceText = priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
      const priceFloat = parseFloat(priceText);
      if (!isNaN(priceFloat)) {
        priceInCents = Math.round(priceFloat * 100);
      }
    }
    
    // Update Klarna placements
    if (priceInCents && window.Klarna?.OnsiteMessaging) {
      document.querySelectorAll('klarna-placement').forEach(placement => {
        placement.setAttribute('data-purchase-amount', priceInCents);
      });
      // Trigger Klarna refresh
      window.Klarna.OnsiteMessaging.refresh();
    }
  }

  handleSubmit(e) {
    let variant = this.currentVariant;
    
    if (!variant) {
      const optionValues = this.getSelectedOptionValues();
      if (optionValues.length === this.getOptionCount()) {
        variant = this.findMatchingVariant(optionValues);
      }
    }
    
    if (variant) {
      this.updateVariantInput(variant.id);
      
      const input = e.target.querySelector('input[name="id"], .product-variant-id');
      if (input && input.value !== String(variant.id)) {
        input.value = String(variant.id);
        input.disabled = false;
      }
    } else {
      e.preventDefault();
      e.stopPropagation();
      alert('Please select a variant option');
    }
  }

  initTooltips() {
    this.container.querySelectorAll('.p2025-variant-tooltip').forEach(trigger => {
      const text = trigger.dataset.tooltip;
      if (!text) return;
      
      const popup = document.createElement('div');
      popup.className = 'p2025-tooltip-popup';
      popup.textContent = text;
      document.body.appendChild(popup);
      
      trigger.addEventListener('mouseenter', () => {
        const rect = trigger.getBoundingClientRect();
        popup.style.left = `${rect.left + rect.width / 2}px`;
        popup.style.top = `${rect.top - 10}px`;
        popup.style.transform = 'translate(-50%, -100%)';
        popup.style.opacity = '1';
      });
      
      trigger.addEventListener('mouseleave', () => {
        popup.style.opacity = '0';
      });
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.p2025-variant-scales').forEach(container => {
    new P2025VariantSelector(container);
  });
});

// Override sidebar behavior for variant info icons
document.addEventListener('click', function(e) {
  const trigger = e.target.closest('[data-sidebar-open-accordion]');
  if (!trigger) return;
  
  const accordionId = trigger.getAttribute('data-sidebar-open-accordion');
  // Only handle variant accordions (those starting with "variant-")
  if (!accordionId || !accordionId.startsWith('variant-')) return;
  
  // Let the default sidebar open, then open the specific variant accordion
  setTimeout(() => {
    const sidebarContent = document.querySelector('.c-sidebar__content-area');
    if (!sidebarContent) return;
    
    // Find and open the specific variant accordion
    const variantAccordion = sidebarContent.querySelector(`[data-accordion-id="${accordionId}"]`);
    if (variantAccordion) {
      // Close any open accordions first
      sidebarContent.querySelectorAll('.c-accordion__item--open').forEach(item => {
        item.classList.remove('c-accordion__item--open');
      });
      // Open the specific variant accordion
      variantAccordion.classList.add('c-accordion__item--open');
    }
  }, 50);
});

// Fix accordion targeting by name instead of index
document.addEventListener('click', function(e) {
  const trigger = e.target.closest('[data-accordion-target]');
  if (!trigger) return;
  
  setTimeout(() => {
    const sidebarContent = document.querySelector('.c-sidebar__content-area');
    if (!sidebarContent) return;
    
    const targetId = trigger.getAttribute('data-accordion-target');
    const targetAccordion = sidebarContent.querySelector(`[data-accordion-id="${targetId}"]`);
    if (targetAccordion) {
      sidebarContent.querySelectorAll('.c-accordion__item--open').forEach(item => {
        item.classList.remove('c-accordion__item--open');
      });
      targetAccordion.classList.add('c-accordion__item--open');
    }
  }, 50);
});

